import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import type { DirectoryUserRow } from '@ai-meeting/shared'
import {
  formatDirectoryValue,
  parseDirectoryEmail,
  type DirectoryValueFormat,
} from '@/utils/directoryValue'

/** Row index standing for "type an address the directory does not have". */
const MANUAL_ROW = -2

/**
 * Narrowest the open panel may be, whatever the control's own width.
 *
 * The assignee column is 176px; a panel that matched it would cut both the
 * search placeholder and most addresses.
 */
const PANEL_MIN_WIDTH_PX = 300

/** Roughly the panel's full height, used only to decide whether to open upwards. */
const PANEL_MAX_HEIGHT_PX = 320

/** Gap between the control and the panel, and the least space kept to the viewport edge. */
const PANEL_GAP_PX = 4

export interface TenantUserSelectProps {
  /** Currently chosen address, or '' when nothing is chosen yet. */
  value: string
  onChange: (email: string) => void
  /** Tenant directory to offer. An empty list leaves only manual entry. */
  users: DirectoryUserRow[]
  /** Shown on the closed control while nothing is chosen. */
  placeholderLabel: string
  /** Placeholder of the search box inside the open list. */
  searchPlaceholder: string
  /** Heading above the tenant rows. */
  tenantGroupLabel: string
  /** Label of the row that switches to free text. */
  manualLabel: string
  /** Accessible name of the free-text field, since the visible label names the control. */
  manualInputLabel: string
  /** Label of the control that returns from free text to the list. */
  backToListLabel: string
  /** Shown when the search matches nobody. */
  noResultsLabel: string
  /**
   * Whether an address outside the tenant can be entered at all.
   *
   * Defaults to true. Turning it off makes this a closed list — only somebody
   * already in the directory can be chosen — which is a real restriction, not a
   * cosmetic one, so the caller has to ask for it explicitly.
   */
  allowManualEntry?: boolean
  /** How a chosen person is written into `value`. Defaults to the address alone. */
  valueFormat?: DirectoryValueFormat
  id?: string
  /**
   * Applies to the free-text field only. The list itself is a button, which the
   * browser does not validate, so a caller that must not submit an empty value
   * has to say so itself — see `AllowedEmailsTab`.
   */
  required?: boolean
  disabled?: boolean
  /** Renders the value as plain text, for a screen that is not in edit mode. */
  readOnly?: boolean
  /** Length cap on the free-text field, for callers whose field has one. */
  maxLength?: number
  /** `data-minutes-field`, so the minutes page can focus this field on a validation error. */
  dataField?: string
  /**
   * Accessible name for the closed control, for a caller with no visible
   * `<label>` pointing at it — a table column heading is not one.
   */
  ariaLabel?: string
}

/**
 * Picks one person out of the Microsoft tenant, with an escape hatch for an
 * address the directory does not hold.
 *
 * Built as a listbox rather than a native `<select>` because the tenant is a few
 * hundred people: a native dropdown renders every one of them in a list that
 * fills the screen and can only be narrowed by typing a name's first letters.
 * This one filters on both name and address as you type, and shows the address
 * under the name rather than crushing both onto one line.
 *
 * Shared because the same choice — "somebody in our tenant, or else type one" —
 * comes up wherever the app has to name a person by email. It is presentational:
 * the caller supplies the list (see `useTenantDirectory`) and every string, so
 * nothing here is bound to one screen or one locale.
 *
 * @param value Controlled address. An address that is not in `users` puts the
 *   control into manual mode by itself, so a rejected submission still shows
 *   what the admin typed instead of resetting to the placeholder.
 */
export default function TenantUserSelect({
  value,
  onChange,
  users,
  placeholderLabel,
  searchPlaceholder,
  tenantGroupLabel,
  manualLabel,
  manualInputLabel,
  backToListLabel,
  noResultsLabel,
  allowManualEntry = true,
  valueFormat = 'email',
  id,
  required,
  disabled,
  readOnly,
  maxLength,
  dataField,
  ariaLabel,
}: TenantUserSelectProps) {
  const listboxId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const manualInputRef = useRef<HTMLInputElement>(null)
  const activeRowRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [manualRequested, setManualRequested] = useState(false)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({})

  const chosenEmail = parseDirectoryEmail(value)
  const selected = value === '' ? undefined : users.find((user) => user.email === chosenEmail)
  const manual = allowManualEntry && (manualRequested || (value !== '' && !selected))

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return users
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(needle) ||
        user.displayName.toLowerCase().includes(needle),
    )
  }, [users, search])

  // Closing on an outside click rather than on blur, so a click that lands on
  // the search box or a row does not race the list closing under the pointer.
  useEffect(() => {
    if (!open) return undefined

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (manualRequested) manualInputRef.current?.focus()
  }, [manualRequested])

  // Keeps the keyboard cursor visible while arrowing through a few hundred rows.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  /**
   * Positions the panel against the viewport rather than against the control.
   *
   * An absolutely-positioned panel is clipped by any scrolling ancestor, and one
   * of this component's homes — the action-items table — is wrapped in
   * `overflow-x-auto`, which CSS turns into clipping on BOTH axes. Fixed
   * positioning takes the viewport as its containing block, so the panel escapes
   * that; the cost is having to follow the control as the page scrolls.
   */
  useLayoutEffect(() => {
    if (!open) return undefined

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return

      const width = Math.max(rect.width, PANEL_MIN_WIDTH_PX)
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpwards = spaceBelow < PANEL_MAX_HEIGHT_PX && rect.top > spaceBelow

      setPanelStyle({
        position: 'fixed',
        width,
        left: Math.max(PANEL_GAP_PX, Math.min(rect.left, window.innerWidth - width - PANEL_GAP_PX)),
        ...(openUpwards
          ? { bottom: window.innerHeight - rect.top + PANEL_GAP_PX }
          : { top: rect.bottom + PANEL_GAP_PX }),
        zIndex: 100,
      })
    }

    place()
    // Capture phase: the control scrolls inside its own container, not the page.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  const openList = () => {
    setSearch('')
    setActiveIndex(0)
    setOpen(true)
  }

  const choose = (user: DirectoryUserRow) => {
    setManualRequested(false)
    setOpen(false)
    onChange(formatDirectoryValue(user, valueFormat))
  }

  const chooseManual = () => {
    setOpen(false)
    setManualRequested(true)
    onChange('')
  }

  /** Rows the arrow keys walk: every match, then the manual row when offered. */
  const rowCount = matches.length + (allowManualEntry ? 1 : 0)
  const indexOfManualRow = allowManualEntry ? matches.length : MANUAL_ROW

  const commitActiveRow = () => {
    if (activeIndex === indexOfManualRow) {
      chooseManual()
      return
    }
    const user = matches[activeIndex]
    if (user) choose(user)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => Math.min(current + 1, rowCount - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      commitActiveRow()
    }
  }

  if (readOnly) {
    // Matches what the minutes table showed before it had a picker: the stored
    // text, nothing interactive.
    return (
      <span title={value || undefined} className="block w-full px-2 py-1 text-left text-sm">
        {value || <span className="text-[var(--text-tertiary)]">{placeholderLabel}</span>}
      </span>
    )
  }

  if (manual) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={manualInputRef}
          type="email"
          id={id}
          required={required}
          maxLength={maxLength}
          data-minutes-field={dataField}
          aria-label={manualInputLabel}
          value={value}
          disabled={disabled}
          // Clamped here as well as through the attribute: `maxlength` governs
          // typing and pasting, but says nothing about a value set any other
          // way, and the caller's field has a hard limit it validates against.
          onChange={(event) =>
            onChange(
              maxLength === undefined
                ? event.target.value
                : event.target.value.slice(0, maxLength),
            )
          }
          className="input-field"
        />
        <button
          type="button"
          onClick={() => {
            setManualRequested(false)
            onChange('')
          }}
          disabled={disabled}
          className="shrink-0 rounded-lg border border-[var(--border-color)] px-2.5 py-2 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
        >
          {backToListLabel}
        </button>
      </div>
    )
  }

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        data-minutes-field={dataField}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        className="input-field flex items-center justify-between gap-2 text-left disabled:opacity-50"
      >
        {/*
          Name over address, the same shape as the rows in the list. On one line
          the pair is too long for the columns this sits in — the assignee cell
          is 176px — and truncation ate the address, which is the half that says
          which of two colleagues with the same name this is.
        */}
        {selected ? (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[var(--text-primary)]">{selected.displayName}</span>
            <span className="truncate text-xs text-[var(--text-tertiary)]">{selected.email}</span>
          </span>
        ) : (
          <span className="truncate text-[var(--text-tertiary)]">{placeholderLabel}</span>
        )}
        <span aria-hidden="true" className="shrink-0 text-[var(--text-tertiary)]">
          ▾
        </span>
      </button>

      {open && (
        <div
          style={panelStyle}
          className="overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-lg"
        >
          <div className="border-b border-[var(--border-color)] p-2">
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setActiveIndex(0)
              }}
              onKeyDown={handleSearchKeyDown}
              className="w-full rounded-lg bg-[var(--bg-tertiary)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] outline-none"
            />
          </div>

          <ul id={listboxId} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {matches.length === 0 && (
              <li className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{noResultsLabel}</li>
            )}

            {matches.length > 0 && (
              <li
                role="presentation"
                className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]"
              >
                {tenantGroupLabel}
              </li>
            )}

            {matches.map((user, index) => (
              <li key={user.email} role="presentation">
                <button
                  ref={index === activeIndex ? activeRowRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={user.email === chosenEmail}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(user)}
                  className={`flex w-full flex-col px-3 py-2 text-left text-sm ${
                    index === activeIndex ? 'bg-[var(--bg-tertiary)]' : ''
                  }`}
                >
                  <span className="truncate text-[var(--text-primary)]">{user.displayName}</span>
                  <span className="truncate text-xs text-[var(--text-tertiary)]">{user.email}</span>
                </button>
              </li>
            ))}

            {allowManualEntry && (
              <li role="presentation" className="border-t border-[var(--border-color)]">
                <button
                  ref={activeIndex === indexOfManualRow ? activeRowRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(indexOfManualRow)}
                  onClick={chooseManual}
                  className={`w-full px-3 py-2 text-left text-sm text-[var(--text-secondary)] ${
                    activeIndex === indexOfManualRow ? 'bg-[var(--bg-tertiary)]' : ''
                  }`}
                >
                  {manualLabel}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
